import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import axios from "axios";

const DEFAULT_FLAGS = {
  voice: false,
  telephony: false,
  knowledge_base: true,
  workflow: true,
};

const FeatureFlagsContext = createContext({
  flags: DEFAULT_FLAGS,
  loading: true,
  isEnabled: () => false,
});

export const FeatureFlagsProvider = ({ children }) => {
  const [flags, setFlags] = useState(DEFAULT_FLAGS);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const fetchFlags = async () => {
      try {
        const response = await axios.get("/api/feature-flags/");
        const data = response.data?.flags || {};
        if (!cancelled) {
          setFlags(data);
        }
      } catch (error) {
        console.error("[FeatureFlags] Failed to load feature flags:", error);
        if (!cancelled) {
          // Fail-safe defaults: keep commonly stable features on, experimental off
          setFlags(DEFAULT_FLAGS);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };
    fetchFlags();
    return () => {
      cancelled = true;
    };
  }, []);

  const isEnabled = useCallback((name) => Boolean(flags[name]), [flags]);
  const value = useMemo(() => ({ flags, loading, isEnabled }), [flags, loading, isEnabled]);

  return (
    <FeatureFlagsContext.Provider value={value}>
      {children}
    </FeatureFlagsContext.Provider>
  );
};

export const useFeatureFlags = () => useContext(FeatureFlagsContext);
